import { CorePermissions, PermissionExplorer } from "app/appPermissions";
import { CurrentLmsCallouts, fetchLmsCallout } from "app/features/callout/calloutSlice";
import { store, useAppSelector } from "app/store";
import { CalloutDto } from "generated/CoreApi_v1";
import useEvent from "hooks/useEvent";
import { TFunction } from "i18next";
import { LateralBlockOption, LateralMenu, LateralMenuBlock, LateralMenuItem } from "layouts/common/MenuCommon";
import { useInsideLayout } from "layouts/common/inside/InsideLayoutContext";
import { useSession } from "providers/SessionProvider";
import React, { useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { LmsPaths } from "../../routes/LmsRouter";

////////////////////////////////////////////////////////////////////////////////


const buildMenuTraining = (pe: PermissionExplorer, t: TFunction): LateralMenuBlock | null => {
    return {
        header: t("App.Menu.Lms.TrainingGroup"),
        blocks: [{
            items: [
                { path: LmsPaths.Home, name: t("App.Menu.Lms.UserTraining") },
                { path: LmsPaths.StudentCatalog, name: t("App.Menu.Lms.Catalog") },
                { path: LmsPaths.StudentLibrary, name: t("App.Menu.Lms.UserLibrary") },
                { path: LmsPaths.StudentMeetings, name: t("App.Menu.Lms.UserEvents") },
                { path: LmsPaths.MyDevPlans, name: t("App.Menu.Lms.DevPlan") },
                { path: LmsPaths.StudentHistory, name: t("App.Menu.Lms.UserHistory") },
            ]
        }]
    };
};

const buildMenuTests = (pe: PermissionExplorer, t: TFunction): LateralMenuBlock | null => {
    return {
        header: t("App.Menu.Lms.KnowledgeTestsGroup"),
        blocks: [{
            items: [
                { path: LmsPaths.NewKnowledgeTest, name: t("App.Menu.Lms.NewKnowledgeTest") },
                //{ path: LmsPaths.KnowledgeTestProgress, name: t("App.Menu.Lms.KnowledgeTestProgress") },
            ]
        }]
    };
};

const buildMenuMonitoring = (pe: PermissionExplorer, t: TFunction, callouts: CalloutDto[]): LateralMenuBlock | null => {

    var hasApprovals = callouts.find(p => (p.id == "PreApprovalPendings" || p.id == "ConclusionFormPendings") && p.active) != null;
    var hasAssCorr = callouts.find(p => p.id == "EssayCorrectionPendings" && p.active) != null;

    const groups: LateralBlockOption[] = [];

    var group1: LateralMenuItem[] = [];
    if (pe.hasOne([CorePermissions.TrainingCoordinate, CorePermissions.TrainingSpecificCoordinateOrInstructor])) {
        group1.push({ path: LmsPaths.Supervisor, name: t("App.Menu.Lms.Tutoring"), menuMatch: [LmsPaths.Supervisor, LmsPaths.SupervisorTrainingPanel], matchCalloutId: ["InstructorPendings"] });
    }

    if (hasAssCorr || pe.hasOne([CorePermissions.TrainingSpecificCoordinate, CorePermissions.TrainingSpecificCoordinateOrInstructor, CorePermissions.AssessmentSpecificPendingCorrection])) {
        group1.push({ path: `${LmsPaths.EvaluationCorrection}/Home`, name: t("App.Menu.Lms.EvaluationCorrection"), matchCalloutId: ["EssayCorrectionPendings"] });
    }

    if (hasApprovals) {
        group1.push({ path: LmsPaths.Approvals, name: t("App.Menu.Lms.Approvals"), matchCalloutId: ["PreApprovalPendings", "ConclusionFormPendings"] });
    }

    var group2: LateralMenuItem[] = [];
    if (pe.hasOne([CorePermissions.TrainingViewDashboard]))
        group2.push({ path: LmsPaths.TrainingPanel, name: t("App.Menu.Lms.InfoPanel") });

    if (pe.hasOne([CorePermissions.TrainingViewDashboard]))
        group2.push({ path: LmsPaths.TrainingIndex, name: t("App.Menu.Lms.TrainingIndex") });


    if (group1.length > 0) groups.push({ items: group1 });
    if (group2.length > 0) groups.push({ items: group2 });

    return groups.length > 0
        ? {
            header: t("App.Menu.Lms.MonitoringGroup"),
            blocks: groups
        }
        : null;
};

const buildMenuManager = (pe: PermissionExplorer, t: TFunction): LateralMenuBlock | null => {
    const groups: LateralBlockOption[] = [];

    var group1: LateralMenuItem[] = [];

    if (pe.hasOne([
        CorePermissions.TrainingManage,
        CorePermissions.TrainingUpdate,
        CorePermissions.TrainingManageClass
    ])) {
        group1.push({ path: LmsPaths.TrainingList, name: t("App.Menu.Lms.TrainingManager"), menuMatch: [LmsPaths.TrainingList] });
    }

    if (pe.hasOne([CorePermissions.TrainingViewInstructorAllocation])) {
        group1.push({ path: LmsPaths.InstructorAllocation, name: t("App.Menu.Lms.InstructorAllocation") });
    }

    //Lista de Espera - NÃO SERÁ IMPLEMENTADO NA RELEASE 1
    //group1.push({ path: LmsPaths.WaitingList, name: t("App.Menu.Lms.WaitingLists") });

    if (pe.hasOne([
        CorePermissions.TrainingManageEnrollments,
        CorePermissions.TrainingManageEnrollmentsTargetAudience,
        CorePermissions.TrainingRevokeExemptExcludeEnrollments,
        CorePermissions.TrainingSpecificCoordinateEnrollUsers
    ])) {
        group1.push({ path: LmsPaths.EnrollmentManager, name: t("App.Menu.Lms.EnrollmentManager") });
    }

    var group2: LateralMenuItem[] = [];
    if (pe.hasOne([CorePermissions.AssessmentManageQuestionBank])) {
        group2.push({ path: LmsPaths.QuestionsBank, name: t("App.Menu.Lms.QuestionsBank") });
    }

    if (pe.hasOne([CorePermissions.AssessmentManageEvaluation])) {
        group2.push({ path: LmsPaths.EvaluationsBank, name: t("App.Menu.Lms.EvaluationsBank") });
    }

    if (pe.hasOne([CorePermissions.AssessmentManageSurvey])) {
        group2.push({ path: LmsPaths.SurveysBank, name: t("App.Menu.Lms.SurveysBank") });
    }

    var group3: LateralMenuItem[] = [];

    if (pe.hasOne([CorePermissions.AssessmentApplySurvey, CorePermissions.AssessmentSuperviseSurvey])) {
        group3.push({ path: LmsPaths.ApplySurveys, name: t("App.Menu.Lms.ApplySurveys") });
    }

    if (pe.hasOne([CorePermissions.AssessmentManageKnowledgeTest])) {
        group3.push({ path: LmsPaths.TopicsChallenge, name: t("App.Menu.Lms.ApplyTests") });
    }

    var group4: LateralMenuItem[] = [];
    if (pe.hasOne([CorePermissions.TrainingManage])) {
        group4.push({ path: LmsPaths.DebugScormTrainings, name: t("App.Menu.Lms.DebugScormTrainings") });
    }

    if (pe.hasOne([CorePermissions.TrainingAdmin])) {
        group4.push({ path: LmsPaths.Settings, name: t("App.Menu.Lms.Settings") });
    }

    if (group1.length > 0) groups.push({ items: group1 });
    if (group2.length > 0) groups.push({ items: group2 });
    if (group3.length > 0) groups.push({ items: group3 });
    if (group4.length > 0) groups.push({ items: group4 });

    return groups.length > 0
        ? {
            header: t("App.Menu.Lms.ManageGroup"),
            blocks: groups
        }
        : null;
};

const buildMenuDataset = (pe: PermissionExplorer, t: TFunction, callouts: CalloutDto[]): LateralMenuBlock[] => {
    const modules: (LateralMenuBlock | null)[] = [
        buildMenuTraining(pe, t),
        buildMenuTests(pe, t),
        buildMenuMonitoring(pe, t, callouts),
        buildMenuManager(pe, t)

    ];
    return modules.filter(p => p != null) as LateralMenuBlock[];
};

////////////////////////////////////////////////////////////////////////////////

export const LmsMenu: React.FC = () => {
    const layout = useInsideLayout();
    const divRef = useRef<HTMLDivElement | null>(null);
    const session = useSession();
    const { t } = useTranslation("shared");
    const callouts = useAppSelector(CurrentLmsCallouts);

    /**
     * Cria os Blocos de Menu que são estáticos.
     **/
    const dataset = useMemo(
        () => buildMenuDataset(session.permissionExplorer, t, callouts)
        , [session.user.permissions, callouts]);

    /**
     * Executa o Polling dos Callout's do Menu de Conhecimento
     **/
    useEffect(() => {
        store.dispatch(fetchLmsCallout({}));
        const timer = setInterval(() => store.dispatch(fetchLmsCallout({})), 60000);
        return () => clearInterval(timer);
    }, []);

    /**
     * Monitora o evento 'click' e 'força' o fechamento do menu ao clicar em uma das opções.
     * */
    useEvent(divRef.current, "click", (e) => {
        if (layout.menuOpened && e.target instanceof HTMLAnchorElement && e.target.tagName == "A") {
            layout.toogleMenu();
        }
    });

    return (
        <div ref={divRef} style={{ height: "inherit" }}>
            <LateralMenu dataset={dataset} callouts={callouts} />
        </div>
    );
};
